import { useState, useEffect, useRef, useCallback } from 'react';
import { useEnhancedAuthContext } from '@/context/auth/EnhancedAuthContext';
import { schedulerApi } from '@/services/api/schedulerApi';
import { leadActivitiesApi } from '@/services/api/leadActivitiesApi';
import { getRecentActivities, LeadActivity } from '@/services/leadActivitiesService';
import { activityApi, ActivityStatsResponse } from '@/services/api/activityApi';
import { leadAssignmentService } from '@/services/leadAssignmentService';
import { leadActivityStatsApi, LeadActivityStatsResponse } from '@/services/api/leadActivityStatsApi';
import { leadStatsService, LeadStatsData } from '@/services/leadStatsService';
import { DashboardActivityItem } from '@/hooks/useDashboardData';
import type { Event, Task } from '@/types/scheduler';
import type { ScheduleEvent, ScheduleTask } from '@/services/api/scheduleApi';

interface UserDashboardStats {
  // Activity-based metrics
  totalActivities: number;
  todaysActivities: number;
  callsAttempted: number;
  callsReached: number;
  emailsSent: number;
  smsSent: number;
  leadsContacted: number;
  leadsCreated: number;
  leadsAssigned: number;
  // Calculated metrics
  callSuccessRate: number;
  conversionRate: number;
  // Task tracking
  tasksCompletedCount: number;
  tasksRemainingCount: number;
  totalTasksCount: number;
  todaysTasksCount: number;
  todaysCompletedTasksCount: number;
  todaysRemainingTasksCount: number;
  // Event tracking
  totalEventsCount: number;
  todaysEventsCount: number;
  todaysCompletedEventsCount: number;
  todaysRemainingEventsCount: number;
  // Geographic breakdown
  usActivities: number;
  otherCountriesActivities: number;
  usLeadsContacted: number;
  otherCountriesLeadsContacted: number;
}

interface TodaysSchedule {
  events: ScheduleEvent[];
  tasks: ScheduleTask[];
}

export const useUserDashboardData = () => {
  const { user } = useEnhancedAuthContext();
  const [stats, setStats] = useState<UserDashboardStats>({
    totalActivities: 0,
    todaysActivities: 0,
    callsAttempted: 0,
    callsReached: 0,
    emailsSent: 0,
    smsSent: 0,
    leadsContacted: 0,
    leadsCreated: 0,
    leadsAssigned: 0,
    callSuccessRate: 0,
    conversionRate: 0,
    tasksCompletedCount: 0,
    tasksRemainingCount: 0,
    totalTasksCount: 0,
    todaysTasksCount: 0,
    todaysCompletedTasksCount: 0,
    todaysRemainingTasksCount: 0,
    totalEventsCount: 0,
    todaysEventsCount: 0,
    todaysCompletedEventsCount: 0,
    todaysRemainingEventsCount: 0,
    usActivities: 0,
    otherCountriesActivities: 0,
    usLeadsContacted: 0,
    otherCountriesLeadsContacted: 0
  });
  const [todaysSchedule, setTodaysSchedule] = useState<TodaysSchedule>({
    events: [],
    tasks: []
  });
  const [activities, setActivities] = useState<DashboardActivityItem[]>([]);
  const [upcomingCalls, setUpcomingCalls] = useState<any[]>([]);
  const [leadActivityStats, setLeadActivityStats] = useState<LeadActivityStatsResponse | null>(null);
  const [leadStats, setLeadStats] = useState<LeadStatsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Request deduplication
  const fetchInProgress = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastUserId = useRef<string | number | null>(null);

  // Helper function to map event types - preserve original types for proper icon display
  const mapEventType = (eventType: string): string => {
    // Return the original event type to preserve specific types like 'follow_up' and 'campaign'
    console.log('🎯 Original event type:', eventType);
    return eventType || 'meeting';
  };

  // Helper function to map task status
  const mapTaskStatus = (status: string): 'pending' | 'in_progress' | 'completed' | 'cancelled' => {
    switch (status) {
      case 'todo':
        return 'pending';
      case 'blocked':
        return 'cancelled';
      case 'pending':
      case 'in_progress':
      case 'completed':
      case 'cancelled':
        return status as 'pending' | 'in_progress' | 'completed' | 'cancelled';
      default:
        return 'pending';
    }
  };

  // Convert API Event to ScheduleEvent
  const convertToScheduleEvent = (event: Event): ScheduleEvent => {
    const eventType = mapEventType(event.event_type || 'meeting');
    console.log('🔄 Converting event:', { id: event.id, title: event.title, eventType, originalType: event.event_type });
    
    return {
      id: event.id.toString(),
      title: event.title,
      description: event.description || '',
      startDate: new Date(event.start_datetime),
      endDate: new Date(event.end_datetime || event.start_datetime),
      eventType: eventType as any,
      location: event.location || '',
      priority: event.priority as 'low' | 'medium' | 'high' | 'urgent',
      status: event.status as 'scheduled' | 'in_progress' | 'completed' | 'cancelled',
      userId: event.user_id,
      isRecurring: false
    };
  };

  // Convert API Task to ScheduleTask
  const convertToScheduleTask = (task: Task): ScheduleTask => ({
    id: task.id.toString(),
    title: task.title,
    description: task.description || '',
    dueDate: task.due_date ? new Date(task.due_date) : undefined,
    priority: task.priority as 'low' | 'medium' | 'high' | 'urgent',
    status: mapTaskStatus(task.status || 'pending'),
    userId: task.user_id
  });

  // Convert LeadActivity to DashboardActivityItem (legacy support)
  const convertToDashboardActivity = (activity: LeadActivity): DashboardActivityItem => {
    let type: 'completed' | 'update' | 'pending';
    switch (activity.activity_status) {
      case 'completed':
        type = 'completed';
        break;
      case 'planned':
        type = 'pending';
        break;
      default:
        type = 'update';
    }

    return {
      id: activity.id,
      type,
      title: `${activity.activity_type.charAt(0).toUpperCase() + activity.activity_type.slice(1)} activity`,
      description: activity.activity_description || `Activity with ${activity.company_name || 'a company'}`,
      time: activity.created_at ? new Date(activity.created_at).toLocaleString() : 'Recently'
    };
  };

  // Convert Activity from API to DashboardActivityItem
  const convertToApiActivityDashboardItem = (activity: any): DashboardActivityItem => {
    let type: 'completed' | 'update' | 'pending';
    switch (activity.activity_status) {
      case 'completed':
        type = 'completed';
        break;
      case 'cancelled':
        type = 'pending';
        break;
      case 'voice_mail':
      case 'no_answer':
      case 'gk':
        type = 'update';
        break;
      default:
        type = 'update';
    }

    const activityTypeMap: { [key: string]: string } = {
      'call': 'Call',
      'email': 'Email',
      'sms': 'SMS',
      'meeting': 'Meeting',
      'note': 'Note'
    };

    const statusMap: { [key: string]: string } = {
      'completed': 'completed',
      'cancelled': 'cancelled',
      'voice_mail': 'voicemail',
      'no_answer': 'no answer',
      'gk': 'gatekeeper'
    };

    const activityTitle = `${activityTypeMap[activity.activity_type] || activity.activity_type} - ${statusMap[activity.activity_status] || activity.activity_status}`;
    
    return {
      id: activity.id.toString(),
      type,
      title: activityTitle,
      description: activity.activity_description || activity.outcome || `${activity.activity_type} activity`,
      time: activity.activity_date ? new Date(activity.activity_date).toLocaleString() : 'Recently'
    };
  };

  const fetchUserDashboardData = useCallback(async () => {
    if (!user?.id) return;

    // Prevent duplicate requests for the same user
    if (fetchInProgress.current && lastUserId.current === user.id) {
      console.log('🚫 Fetch already in progress for user:', user.id, '- skipping duplicate request');
      return;
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Set up new abort controller
    abortControllerRef.current = new AbortController();
    fetchInProgress.current = true;
    lastUserId.current = user.id;

    console.log('🔄 Starting dashboard data fetch for user:', user.id);
    setIsLoading(true);
    setError(null);

    try {
      const userId = typeof user.id === 'string' ? parseInt(user.id) : user.id;
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

      console.log('📅 Date range:', { todayStart, todayEnd });

      // Fetch data in parallel with proper error handling and abort signal
      const [
        todayActivityStats,
        todaysEvents,
        allEvents,
        allTasks,
        recentActivitiesData,
        upcomingCallsData,
        assignedLeadsData,
        createdLeadsData,
        leadStatsData,
        leadStatsServiceData
      ] = await Promise.all([
        // Get today's activity statistics
        activityApi.getTodayActivityStats(userId).catch(err => {
          if (err.name === 'AbortError') throw err;
          console.error('❌ Activity stats fetch failed:', err);
          return null;
        }),
        // Get today's events from API
        schedulerApi.listEvents({ 
          user_id: userId, 
          start_date: todayStart.toISOString(), 
          end_date: todayEnd.toISOString() 
        }).catch(err => {
          if (err.name === 'AbortError') throw err;
          console.error('❌ Events fetch failed:', err);
          return [];
        }),
        // Get all user events from API
        schedulerApi.listEvents({ 
          user_id: userId 
        }).catch(err => {
          if (err.name === 'AbortError') throw err;
          console.error('❌ All events fetch failed:', err);
          return [];
        }),
        // Get all user tasks from API
        schedulerApi.listTasks({ 
          user_id: userId 
        }).catch(err => {
          if (err.name === 'AbortError') throw err;
          console.error('❌ Tasks fetch failed:', err);
          return [];
        }),
        // Get recent activities
        activityApi.getRecentUserActivities(userId, 10).catch(err => {
          if (err.name === 'AbortError') throw err;
          console.error('❌ Activities fetch failed:', err);
          return { activities: [] };
        }),
        // Get upcoming calls from API with user ID
        leadActivitiesApi.getUpcomingCalls(5, userId).catch(err => {
          if (err.name === 'AbortError') throw err;
          console.error('❌ Upcoming calls fetch failed:', err);
          return [];
        }),
        // Get leads assigned to this user this month
        leadAssignmentService.getAssignedLeadsByMonth(userId, new Date().toISOString().substring(0, 7)).catch(err => {
          if (err.name === 'AbortError') throw err;
          console.error('❌ Assigned leads fetch failed:', err);
          return { leads: [], total: 0 };
        }),
        // Get leads created by this user
        leadAssignmentService.getCreatedLeadsByMonth(userId, new Date().toISOString().substring(0, 7)).catch(err => {
          if (err.name === 'AbortError') throw err;
          console.error('❌ Created leads fetch failed:', err);
          return { leads: [], total: 0 };
        }),
        // Get lead activity stats (pipeline, stale, recency)
        leadActivityStatsApi.getStats(userId).catch(err => {
          if (err.name === 'AbortError') throw err;
          console.error('❌ Lead activity stats fetch failed:', err);
          return null;
        }),
        // Get lead stats (countries, score distribution)
        leadStatsService.getLeadStats(userId.toString()).catch(err => {
          if (err.name === 'AbortError') throw err;
          console.error('❌ Lead stats fetch failed:', err);
          return null;
        })
      ]);

      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        console.log('🚫 Request was aborted');
        return;
      }

      console.log('📊 API Results:', {
        activityStats: todayActivityStats,
        eventsCount: Array.isArray(todaysEvents) ? todaysEvents.length : 0,
        tasksCount: Array.isArray(allTasks) ? allTasks.length : 0,
        activitiesCount: recentActivitiesData?.activities?.length || 0,
        callsCount: Array.isArray(upcomingCallsData) ? upcomingCallsData.length : 0,
        assignedLeadsCount: assignedLeadsData?.total || 0,
        createdLeadsCount: createdLeadsData?.total || 0
      });

      // Calculate stats from activity data
      const activityMetrics = todayActivityStats?.activity_metrics;
      const allTasksArray = Array.isArray(allTasks) ? allTasks : [];
      const allEventsArray = Array.isArray(allEvents) ? allEvents : [];
      const todaysEventsArray = Array.isArray(todaysEvents) ? todaysEvents : [];
      
      // All tasks stats
      const allCompletedTasks = allTasksArray.filter(task => task.status === 'completed');
      const allPendingTasks = allTasksArray.filter(task => task.status === 'pending' || task.status === 'in_progress');
      
      // All events stats
      const allCompletedEvents = allEventsArray.filter(event => event.status === 'completed');
      const allPendingEvents = allEventsArray.filter(event => event.status === 'scheduled' || event.status === 'in_progress');
      
      // Today's events stats
      const todaysCompletedEvents = todaysEventsArray.filter(event => event.status === 'completed');
      const todaysRemainingEvents = todaysEventsArray.filter(event => event.status === 'scheduled' || event.status === 'in_progress');
      
      // Today's tasks stats
      const todaysTasks = allTasksArray.filter(task => {
        if (!task.due_date) return false;
        const dueDate = new Date(task.due_date);
        return dueDate >= todayStart && dueDate < todayEnd;
      });
      
      const todaysCompletedTasks = todaysTasks.filter(task => task.status === 'completed');
      const todaysRemainingTasks = todaysTasks.filter(task => task.status === 'pending' || task.status === 'in_progress');

      // Calculate success rates
      const callSuccessRate = activityMetrics?.calls_attempted > 0 
        ? Math.round((activityMetrics.calls_reached / activityMetrics.calls_attempted) * 100)
        : 0;
        
      const conversionRate = activityMetrics?.leads_contacted > 0
        ? Math.round((activityMetrics.leads_created / activityMetrics.leads_contacted) * 100)
        : 0;

      console.log('📊 Task & Event Statistics:', {
        totalTasks: allTasksArray.length,
        allCompletedTasks: allCompletedTasks.length,
        allPendingTasks: allPendingTasks.length,
        todaysTasks: todaysTasks.length,
        todaysCompletedTasks: todaysCompletedTasks.length,
        todaysRemainingTasks: todaysRemainingTasks.length,
        totalEvents: allEventsArray.length,
        allCompletedEvents: allCompletedEvents.length,
        allPendingEvents: allPendingEvents.length,
        todaysEvents: todaysEventsArray.length,
        todaysCompletedEvents: todaysCompletedEvents.length,
        todaysRemainingEvents: todaysRemainingEvents.length,
        tasksWithDueDates: allTasksArray.filter(t => t.due_date).length,
        tasksWithoutDueDates: allTasksArray.filter(t => !t.due_date).length
      });

      setStats({
        totalActivities: activityMetrics?.total_activities || 0,
        todaysActivities: activityMetrics?.total_activities || 0, // Today's data from the API
        callsAttempted: activityMetrics?.calls_attempted || 0,
        callsReached: activityMetrics?.calls_reached || 0,
        emailsSent: activityMetrics?.emails_sent || 0,
        smsSent: activityMetrics?.sms_sent || 0,
        leadsContacted: activityMetrics?.leads_contacted || 0,
        leadsCreated: activityMetrics?.leads_created || 0,
        leadsAssigned: activityMetrics?.leads_assigned || 0,
        callSuccessRate,
        conversionRate,
        tasksCompletedCount: allCompletedTasks.length,
        tasksRemainingCount: allPendingTasks.length,
        totalTasksCount: allTasksArray.length,
        todaysTasksCount: todaysTasks.length,
        todaysCompletedTasksCount: todaysCompletedTasks.length,
        todaysRemainingTasksCount: todaysRemainingTasks.length,
        totalEventsCount: allEventsArray.length,
        todaysEventsCount: todaysEventsArray.length,
        todaysCompletedEventsCount: todaysCompletedEvents.length,
        todaysRemainingEventsCount: todaysRemainingEvents.length,
        usActivities: activityMetrics?.us_activities || 0,
        otherCountriesActivities: activityMetrics?.other_countries_activities || 0,
        usLeadsContacted: activityMetrics?.us_leads_contacted || 0,
        otherCountriesLeadsContacted: activityMetrics?.other_countries_leads_contacted || 0
      });

      // Set today's schedule
      const convertedEvents = todaysEventsArray.map(convertToScheduleEvent);
      
      // Use the same todaysTasks calculation from above
      const convertedTasks = todaysTasks.map(convertToScheduleTask);

      setTodaysSchedule({
        events: convertedEvents,
        tasks: convertedTasks
      });

      // Set activities
      const activitiesArray = recentActivitiesData?.activities || [];
      const dashboardActivities = activitiesArray.map(activity => convertToApiActivityDashboardItem(activity));
      setActivities(dashboardActivities);

      // Set upcoming calls
      const callsArray = Array.isArray(upcomingCallsData) ? upcomingCallsData : [];
      console.log('📞 Setting upcoming calls:', callsArray);
      setUpcomingCalls(callsArray);

      setLeadActivityStats(leadStatsData);
      setLeadStats(leadStatsServiceData);

      console.log('✅ Dashboard data fetch completed for user:', user.id);

    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('🚫 Dashboard fetch was aborted');
        return;
      }
      
      console.error('Error fetching user dashboard data:', err);
      setError('Failed to load dashboard data');
      
      // Set fallback data
      setStats({
        totalActivities: 8,
        todaysActivities: 8,
        callsAttempted: 6,
        callsReached: 0,
        emailsSent: 2,
        smsSent: 0,
        leadsContacted: 4,
        leadsCreated: 2,
        leadsAssigned: 2,
        callSuccessRate: 0,
        conversionRate: 50,
        tasksCompletedCount: 8,
        tasksRemainingCount: 6,
        totalTasksCount: 14,
        todaysTasksCount: 3,
        todaysCompletedTasksCount: 2,
        todaysRemainingTasksCount: 1,
        totalEventsCount: 8,
        todaysEventsCount: 2,
        todaysCompletedEventsCount: 1,
        todaysRemainingEventsCount: 1,
        usActivities: 0,
        otherCountriesActivities: 8,
        usLeadsContacted: 0,
        otherCountriesLeadsContacted: 4
      });
    } finally {
      fetchInProgress.current = false;
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchUserDashboardData();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      fetchInProgress.current = false;
    };
  }, [fetchUserDashboardData]);

  return {
    stats,
    todaysSchedule,
    activities,
    upcomingCalls,
    leadActivityStats,
    leadStats,
    isLoading,
    error,
    refetchData: fetchUserDashboardData
  };
};